// The route being edited on the Plan map (one at a time), with its undo history. PlanPage writes each change
// into the trail's draft so the map, the card and Save all see it.
import { useSyncExternalStore } from 'react';
import type { History, RouteEdit } from './editing';
import { fromApi, record, redo, startHistory, undo } from './editing';
import type { ApiTrail } from './trail';

export interface EditSession {
  guid: string;
  /** The route as it was when editing started (name, notes and so on are kept from it). */
  base: ApiTrail;
  /** The route's points as editing started, to tell whether anything has changed. */
  initial: RouteEdit;
  history: History<RouteEdit>;
}

let session: EditSession | null = null;
let committer: ((session: EditSession) => void) | undefined;
const listeners = new Set<() => void>();

function set(next: EditSession | null) {
  session = next;
  // Straight away, not in an effect: a split commits one route and starts editing another at once.
  if (next) committer?.(next);
  for (const listener of listeners) listener();
}

/** Called with the session after every change, to write it where the rest of the page reads it. */
export function setCommitter(commit: ((session: EditSession) => void) | undefined) {
  committer = commit;
}

export function startEditing(guid: string, base: ApiTrail) {
  const initial = fromApi(base);
  set({ guid, base, initial, history: startHistory(initial) });
}

export const stopEditing = () => set(null);

/** Carries on editing a route recovered from an earlier session: one undo step back to `base`. */
export function resumeEditing(guid: string, base: ApiTrail, present: RouteEdit) {
  const initial = fromApi(base);
  set({ guid, base, initial, history: record(startHistory(initial), present) });
}

/** The route as it stands has been saved: it becomes the unchanged state, and undo still works. */
export function markSaved(guid: string, saved: ApiTrail) {
  if (session?.guid === guid) set({ ...session, base: saved, initial: session.history.present });
}

/** Applies a change to the route being edited (a no-op change isn't recorded). */
export function change(edit: (route: RouteEdit) => RouteEdit) {
  if (!session) return;
  set({ ...session, history: record(session.history, edit(session.history.present)) });
}

/**
 * Updates the route as it stands without adding an undo step: for snapped paths arriving after the change that
 * asked for them, so undo goes back past the change itself.
 */
export function amend(edit: (route: RouteEdit) => RouteEdit) {
  if (!session) return;
  const present = edit(session.history.present);
  if (present !== session.history.present) set({ ...session, history: { ...session.history, present } });
}

export function undoEdit() {
  if (session) set({ ...session, history: undo(session.history) });
}

export function redoEdit() {
  if (session) set({ ...session, history: redo(session.history) });
}

export const getEditSession = () => session;

export function subscribeEditSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useEditSession(): EditSession | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => session,
  );
}
